import React, { useContext, useEffect, useRef, useState } from 'react';
import { Text, Flex, Icon, Menu, MenuButton, Portal, MenuList, MenuItem, IconButton, useDisclosure, Box, Button, Tooltip } from '@chakra-ui/react';
import { MdArrowForward, MdDevices, MdDragIndicator, MdFace, MdMoreVert, MdOutlineEdit, MdOutlineLayers, MdOutlineLayersClear, MdOutlineUndo, MdArrowOutward, MdSync, MdOutlineWarning } from 'react-icons/md';
import InsightTags from '@/insight/components/insightTags';
import useExperienceApi from '@/experiences/lib/useExperienceApi';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/modal';
import PersonaProvider from '@/persona/lib/personaProvider';
import PersonaThumbnail from '@/persona/components/personaThumbnail';
import { PersonaContext } from '../../lib/personaContext';
import { PersonaPageContext } from '../lib/personaPageContext';
import ExperienceGroupInPersonaNameForm from './ExperienceGroupInPersonaNameForm';
import MoveGroupInPersonaToGroupForm from './moveGroupInPersonaToGroupForm';
import ProductLogo from '@/products/components/productLogo';
import ProductProvider from '@/products/lib/productProvider';
import { Link, useNavigate } from 'react-router-dom';
import SharedExperienceTag from '@/components/sharedExperienceTag';
import useTranslation from '../../../lib/i18n/useTranslation';

export default function ExperienceGroupInPersonaListItem(props) {
  const { t } = useTranslation();
  const {
    personaId,
    rights,
  } = useContext(PersonaContext);
  const {
    refreshExperienceList,
    refreshProductList,
    refreshSharedExperienceList,
    experienceGroupList,
  } = useContext(PersonaPageContext);
  const {
    data,
    dragHoverEnable,
    dragHover = false
  } = props;
  const experienceApi = useExperienceApi();
  const navigate = useNavigate();
  const [personaList, setPersonaList] = useState(false);
  const [productList, setProductList] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const modalEditGroupName = useDisclosure();
  const modalMoveToGroup = useDisclosure();
  const modalConvertToSharedExperience = useDisclosure();
  const [isConverting, setIsConverting] = useState(false);
  const ref1 = useRef();
  const ref2 = useRef();
  
  const ungroup = async () => {
    try {
      await experienceApi.ungroupExperienceGroupInPersona(data.id)
      await Promise.all([
        refreshExperienceList(),
        refreshProductList()
      ])
    } catch (error) {
      console.error(error);
    }
  }
  
  const convertToSharedExperience = async () => {
    try {
      setIsConverting(true);
      await experienceApi.convertPersonaGroupToSharedExperience(personaId, data.id);
      modalConvertToSharedExperience.onClose();
      await Promise.all([
        refreshExperienceList(),
        refreshProductList(),
        refreshSharedExperienceList()
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsConverting(false);
    }
  }
  
  useEffect(() => {
    if (data) {
      const personas = data.personas ? 
        (Array.isArray(data.personas) ? data.personas : [data.personas]) : 
        [];
      const products = data.products ? 
        (Array.isArray(data.products) ? data.products : [data.products]) : 
        [];
      if (personas.length > 0) {
        setPersonaList(personas)
      } else {
        setPersonaList(false)
      }
      if (products.length > 0) {
        setProductList(products)
      } else {
        setProductList(false)
      }
    }
  }, [data]);
  
  if (!data || (!data.group?.name && !data.name)) {
    return null;
  }
  
  return (
    <>
      <Flex
        px={3}
        py={2}
        backgroundColor={(dragHoverEnable && dragHover) ? "background.blue" : "white"}
        role={'group'}
        _hover={{
          background: "background.grey",
        }}
        height={"90px"}
        position={"relative"}
        justifyContent={"space-between"}
      >
        {rights?.can_update && (
          <Flex
            position={"absolute"}
            top="0"
            left={"0"}
            height={"100%"}
            opacity={0}
            _groupHover={{ opacity: '1' }}
            cursor={"grab"}
            py={2}
            pr={3}
          >
            <Icon
              as={MdDragIndicator}
              boxSize={'20px'}
              color={"text.light.grey"}
              mr={2}
            />
          </Flex>
        )}
        <Flex
          flexDirection={"column"}
          flex={1}
        >
          <Flex
            flex={1}
            alignItems={"flex-start"}
          >
            <Flex
              alignItems={"flex-start"}
              mb={1}
              flex={1}
            >
              <Icon
                as={MdOutlineLayers}
                boxSize={'15px'}
                mr={2}
                mt={"3px"}
                color="text.medium.blue"
              />
              <Flex flexDirection={"column"}>
                <Text
                  noOfLines={2}
                  color="text.medium.blue"
                  fontWeight={"500"}
                >{data.group?.name || data.name}</Text>
                <Flex alignItems="center"
                  gap={2}
                  mt={1}
                >
                  {data?.shared_experiences?.name && (
                    <SharedExperienceTag 
                      sharedExperienceName={data.shared_experience_name} 
                      size="sm"
                    />
                  )}
                  <Text
                    fontSize={"12px"}
                    color="text.medium.grey"
                  >{data.experience_count} Experience{(data.experience_count > 0 ? 's' : '')}</Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex
              flexDirection={"column"}
              alignSelf={"stretch"}
              height={"100%"}
              alignItems={"flex-end"}
            >
              <Flex flex={1} alignItems={"flex-start"}>
                {data?.insights_count && (
                  <InsightTags
                    nbPostiveFeedback={data?.insights_count.positive_count || 0}
                    nbNegativeFeedback={data?.insights_count.negative_count || 0}
                    nbImprovementFeedback={data?.insights_count.improvement_count || 0}
                    nbBehavior={data?.insights_count.behavior_count || 0}
                    nbMotivation={data?.insights_count.motivation_count || 0}
                  />
                )}
            </Flex>
            <Flex>
              {(personaList && personaList.length > 0) && (
                <Flex
                  alignItems={"center"}
                  mr={3}
                >
                  <Icon
                    as={MdFace}
                    size="20"
                    color="text.medium.grey"
                  />
                  {personaList.map((p, i) => (
                    <Tooltip
                      label={p.name}
                      key={i}
                    >
                      <Link to={`/persona/${p.id}`} onClick={(e) => {
                        e.stopPropagation()
                      }}>
                        <Flex
                          ml={2}
                        >
                          <PersonaProvider
                            personaId={p.id}
                            originalPersona={p}
                            needRights={false}
                            needRole={false}
                            needEditionRequest={false}
                          >
                            <PersonaThumbnail
                              size={20}
                              hasAcronym={true}
                            />
                          </PersonaProvider>
                        </Flex>
                      </Link>
                    </Tooltip>
                  ))}
                </Flex>
              )}
              {(productList && productList.length > 0) && (
                <Flex alignItems={"center"}>
                  <Icon
                    as={MdDevices}
                    size="20"
                    color="text.medium.grey"
                  />
                  {productList && productList.map((p, i) => (
                    <Tooltip
                      label={p.name}
                      key={i}
                    >
                      <Link to={`/products/${p.id}`} onClick={(e) => {
                        e.stopPropagation()
                      }}>
                        <Flex
                          ml={2}
                        >
                          <ProductProvider
                            productId={p.id}
                            orignalProduct={p}
                            needRights={false}
                            needRole={false}
                            needEditionRequest={false}
                          >
                            <ProductLogo
                              size={20}
                              hasAcronym={true}
                            />
                          </ProductProvider>
                        </Flex>
                      </Link>
                    </Tooltip>
                  ))}
                </Flex>
              )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Flex>
          <Flex
            flex={1}
            alignItems={"center"}
          >
        </Flex>
        <Flex>
          <Flex
            height={"100%"}
            pl={3}
            alignItems={"center"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
              <Menu>
                {({ isOpen }) => (
                  <>
                    <MenuButton
                      isActive={isOpen}
                      as={IconButton}
                      icon={<MdMoreVert />}
                      fontSize={"18px"}
                      size="md"
                      variant='greyTransparent'
                    />
                    <Portal>
                  <MenuList fontSize={"14px"}>
                    <MenuItem onClick={() => {
                      navigate(`/persona/${personaId}?group-id=${data.id}`);
                    }} icon={<MdArrowForward />}>
                      Open group
                          </MenuItem>
                    {rights?.can_update && (
                      <>
                        {!data.shared_experiences_id && (
                          <MenuItem onClick={modalEditGroupName.onOpen} icon={<MdOutlineEdit />}>
                            {t('group.editGroupName')}
                          </MenuItem>
                        )}
                        {!data.shared_experiences_id && (
                          <MenuItem
                            onClick={modalMoveToGroup.onOpen}
                          >
                            <Icon as={MdArrowOutward} mr={2} color="text.medium.dark" />
                            <Text color="text.medium.dark">{t('group.moveToGroup')}</Text>
                          </MenuItem>
                        )}
                        {!data.shared_experiences_id && (
                          <MenuItem onClick={ungroup} icon={<MdOutlineLayersClear />}>
                            {t('group.ungroup')}
                          </MenuItem>
                        )}
                        {!data.shared_experiences_id && (
                          <MenuItem onClick={modalConvertToSharedExperience.onOpen} icon={<MdSync />}>
                            {t('group.convertToSharedExperience')}
                          </MenuItem>
                        )}
                      </>
                        )}
                      </MenuList>
                    </Portal>
                  </>
                )}
              </Menu>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Modal isOpen={modalEditGroupName.isOpen} onClose={modalEditGroupName.onClose}>
          <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('group.editGroupName')}</ModalHeader>
          <ModalBody>
              <Box p={3}>
                <ExperienceGroupInPersonaNameForm
                group={{ name: data.group?.name || data.name }}
                  onSubmitting={() => {
                    setIsAdding(true);
                  }}
                onSubmitted={async () => {
                    setIsAdding(false);
                  modalEditGroupName.onClose();
                  await refreshExperienceList();
                  }}
                onValidityChange={setIsValid}
                groupId={data.id}
                  ref={ref1}
                hideSave={true}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='blueOutline'
                onClick={modalEditGroupName.onClose}
            >{t('common.cancel')}</Button>
              <Button
                variant="blueFill"
                ml={2}
                isLoading={isAdding}
                isDisabled={!isValid}
                onClick={() => {
                  ref1.current.click();
                }}>{t('common.save')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      <Modal isOpen={modalMoveToGroup.isOpen} onClose={modalMoveToGroup.onClose}>
          <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('group.moveToGroup')}</ModalHeader>
          <ModalBody>
              <Box p={3}>
                <MoveGroupInPersonaToGroupForm
                experienceGroupList={experienceGroupList}
                  onSubmitting={() => {
                    setIsAdding(true);
                  }}
                onSubmitted={async () => {
                    setIsAdding(false);
                  modalMoveToGroup.onClose();
                  await refreshExperienceList();
                  }}
                onValidityChange={setIsValid}
                groupId={data.id}
                  ref={ref2}
                hideSave={true}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='blueOutline'
                onClick={modalMoveToGroup.onClose}
            >{t('common.cancel')}</Button>
              <Button
                variant="blueFill"
                ml={2}
                isLoading={isAdding}
                isDisabled={!isValid}
                onClick={() => {
                  ref2.current.click();
                }}>{t('common.save')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      <Modal isOpen={modalConvertToSharedExperience.isOpen} onClose={modalConvertToSharedExperience.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('group.convertToSharedExperience')}</ModalHeader>
          <ModalBody p={3}>
            <Flex
              border="1px"
              borderColor="divider.grey"
              borderRadius="2px"
              backgroundColor="white"
              alignItems={'center'}
              p={3}
            >
              <Icon
                as={MdOutlineWarning}
                color={'text.medium.yellow'}
                boxSize={'18px'}
                mr={3}
              />
              <Text textStyle="body" color={'text.dark.grey'}>
                Are you sure you want to convert the group "{data.group?.name || data.name}" into a shared experience? This action cannot be undone.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='blueOutline'
              onClick={modalConvertToSharedExperience.onClose}
            >{t('common.cancel')}</Button>
            <Button
              variant="yellowFill"
              ml={2}
              isLoading={isConverting}
              onClick={convertToSharedExperience}
            >{t('common.convert')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}